import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getSupabaseUrl, getSupabaseAnonKey } from '../lib/environments';
import { returnToApp } from '../lib/returnToApp';
import { useAuth } from '../context/AuthContext';

type Step = 'loading' | 'info' | 'accepting' | 'success' | 'error';

export const AlbumInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { session } = useAuth();
  const [step, setStep] = useState<Step>('loading');
  const [error, setError] = useState<string | null>(null);
  const [albumTitle, setAlbumTitle] = useState('');
  const [role, setRole] = useState('');
  const [albumId, setAlbumId] = useState('');
  const [usesRemaining, setUsesRemaining] = useState<number | null>(null);

  useEffect(() => {
    loadInvite();
  }, [token]);

  const loadInvite = async () => {
    if (!token) {
      setError('Invalid invitation link');
      setStep('error');
      return;
    }

    try {
      const res = await fetch(
        `${getSupabaseUrl()}/functions/v1/album-qr-invite-api?token=${token}`,
        { headers: { 'Authorization': `Bearer ${getSupabaseAnonKey()}` } }
      );
      const data = await res.json();

      if (!res.ok || !data.valid) {
        setError(data.error || 'This invitation is invalid or has expired.');
        setStep('error');
        return;
      }

      setAlbumTitle(data.album_title);
      setRole(data.role);
      setAlbumId(data.album_id);
      setUsesRemaining(data.uses_remaining);
      setStep('info');
    } catch (err) {
      console.error('Error loading album invite:', err);
      setError('Failed to load invitation');
      setStep('error');
    }
  };

  const handleAccept = async () => {
    if (!session) {
      returnToApp({ path: `album-invite/${token}` });
      return;
    }

    setStep('accepting');
    try {
      const res = await fetch(
        `${getSupabaseUrl()}/functions/v1/album-qr-invite-api?action=accept`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ token }),
        }
      );
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Failed to accept invitation');
        setStep('info');
        return;
      }

      setStep('success');
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setStep('info');
    }
  };

  const openInApp = () => {
    returnToApp({ path: `album-invite/${token}` });
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-8 text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-lg">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="text-6xl mb-4">😔</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Unable to Join Album</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">Please ask the album owner for a new invitation.</p>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">You're In!</h1>
          <p className="text-gray-600 mb-6">
            You've joined <strong>{albumTitle}</strong> as a {role}.
          </p>
          <button
            onClick={openInApp}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
          >
            Open in Kizu App
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
        <div className="text-6xl mb-4">📸</div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Album Invitation</h1>
        <p className="text-gray-600 mb-6">
          You've been invited to join <strong>{albumTitle}</strong> as a {role}.
        </p>
        {usesRemaining !== null && (
          <p className="text-sm text-gray-500 mb-4">{usesRemaining} spots remaining</p>
        )}
        {error && (
          <p className="text-red-500 text-sm mb-4">{error}</p>
        )}
        <div className="space-y-3">
          {session ? (
            <button
              onClick={handleAccept}
              disabled={step === 'accepting'}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {step === 'accepting' ? 'Joining...' : 'Accept Invitation'}
            </button>
          ) : (
            <button
              onClick={openInApp}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              Open in Kizu App
            </button>
          )}
        </div>
        <div className="mt-6 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            Don't have the app?{' '}
            <a href="https://kizu.online/get-kizu" className="text-blue-500 hover:underline">
              Download Kizu
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
